import { Link } from "@tanstack/react-router";
import { Section } from "./Section";
import { RevealOnScroll } from "./RevealOnScroll";
import { services } from "@/lib/services-registry";

export type RelatedItem = {
  id: keyof typeof services;
  subtitleDe: string;
  subtitleEn: string;
};

type Props = {
  locale: "de" | "en";
  fromId: string;
  number?: string;
  items: RelatedItem[];
  variant?: "default" | "tinted";
};

const labels = {
  de: {
    eyebrow: "Weiter vertiefen",
    headline: "Verwandte Leistungen",
    lead: "Diese Disziplinen wirken im Zusammenspiel.",
    typeLeistung: "Leistung",
    typeTool: "Tool",
  },
  en: {
    eyebrow: "Go deeper",
    headline: "Related services",
    lead: "These disciplines work in interplay.",
    typeLeistung: "Service",
    typeTool: "Tool",
  },
};

export function RelatedServices({ locale, fromId, number, items, variant = "tinted" }: Props) {
  const L = labels[locale];
  return (
    <Section
      variant={variant}
      number={number}
      kicker={L.eyebrow}
      headline={L.headline}
      caption={L.lead}
    >
      <RevealOnScroll>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {items.map((it) => {
            const svc = services[it.id];
            if (!svc) return null;
            const target = svc[locale];
            const subtitle = locale === "de" ? it.subtitleDe : it.subtitleEn;
            const typeLabel = svc.type === "tool" ? L.typeTool : L.typeLeistung;
            return (
              <Link
                key={it.id}
                to={target.path}
                data-cross-link={`from:${fromId}-to:${it.id}`}
                className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                  {typeLabel}
                </p>
                <h3 className="mt-3 font-display text-xl font-semibold leading-snug text-ink">
                  {target.name}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{subtitle}</p>
                <span
                  aria-hidden
                  className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-lime-deep"
                >
                  →
                </span>
              </Link>
            );
          })}
        </div>
      </RevealOnScroll>
    </Section>
  );
}
